import { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Link, NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import './App.css';
import './pages/SimulationPage.css';
import './workspace.css';
import { ThemeProvider, ThemeToggle } from './components/Theme';
import { Brand } from './components/Brand';
import { WorkspaceIcon } from './components/WorkspaceIcon';
import { WorkspaceFeaturePage } from './pages/WorkspaceFeaturePage';
import { SystemAuditPage } from './pages/SystemAuditPage';
import { AccessHelpPage } from './pages/AccessHelpPage';
import { SimulationPage } from './pages/SimulationPage';
import { AuthProvider, RequireAdmin, RequireAuth, useAuth } from './api/auth';
import { ToastProvider } from './components/DesignSystem';
import { ApprovedReportsPage } from './pages/ApprovedReportsPage';
import { AuthorisedContractsPage } from './pages/AuthorisedContractsPage';
import { CrmPage } from './pages/CrmPage';
import { DashboardPage } from './pages/DashboardPage';
import { LandingPage } from './pages/landing/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { RightsPage } from './pages/RightsPage';
import { SignupPage } from './pages/SignupPage';
import { UploadContractPage } from './pages/UploadContractPage';
import { UploadsInProgressPage } from './pages/UploadsInProgressPage';
import { UserAccessPage } from './pages/UserAccessPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

interface RouteMeta {
  section: string;
  page: string;
  live?: boolean;
}

const ROUTE_META: Record<string, RouteMeta> = {
  '/dashboard': { section: 'Intelligence', page: 'Dashboard', live: true },
  '/upload': { section: 'Contracts', page: 'Upload' },
  '/uploads-in-progress': { section: 'Contracts', page: 'In Progress' },
  '/authorised-contracts': { section: 'Contracts', page: 'Authorised' },
  '/rights': { section: 'Rights', page: 'Tracker' },
  '/crm': { section: 'Sales', page: 'CRM' },
  '/approved-reports': { section: 'Reports', page: 'Approved' },
  '/org/users': { section: 'Organisation', page: 'User Access' },
};

const NAV_GROUPS = [
  {
    label: 'Core',
    links: [
      { to: '/dashboard', label: 'Financial Dashboard', icon: 'dashboard' },
      { to: '/upload', label: 'Upload Contract', icon: 'upload' },
      { to: '/uploads-in-progress', label: 'Uploads in Progress', icon: 'upload' },
      { to: '/authorised-contracts', label: 'Authorised Contracts', icon: 'contracts' },
      { to: '/crm', label: 'Sales CRM', icon: 'sales' },
      { to: '/rights', label: 'Rights Management', icon: 'rights' },
    ],
  },
  {
    label: 'Reports & Data',
    links: [
      { to: '/report-simulations', label: 'Report Simulations', icon: 'reports' },
      { to: '/approved-reports', label: 'Approved Reports', icon: 'reports' },
      { to: '/general-ledger', label: 'General Ledger', icon: 'ledger' },
      { to: '/synced-erp', label: 'Synced ERP', icon: 'sync' },
    ],
  },
  {
    label: 'Tools',
    links: [
      { to: '/calculator-rules', label: 'Calculator Rules', icon: 'ledger' },
      { to: '/insights', label: 'Insights', icon: 'insights' },
      { to: '/catalog-intelligence', label: 'Catalog Intelligence', icon: 'catalog' },
      { to: '/contract-diff', label: 'Contract Diff', icon: 'diff' },
    ],
  },
];

function ShellLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const sidebar = useRef<HTMLElement>(null);
  useEffect(() => {
    sidebar.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [location.pathname]);
  const admin = user?.roles.includes('admin');
  const allLinks = NAV_GROUPS.flatMap((group) => group.links);
  const active = allLinks.find((link) => location.pathname === link.to);
  const group = NAV_GROUPS.find((item) => item.links.some((link) => link.to === active?.to));
  const meta = ROUTE_META[location.pathname];
  return (
    <div className="App rfp-shell">
      <a className="wf-skip" href="#main-content">
        Skip to content
      </a>
      <header className="wf-workspace-header">
        <button
          type="button"
          className="wf-menu-toggle"
          aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={menuOpen}
          aria-controls="workspace-navigation"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ☰
        </button>
        <Brand />
        <div className="wf-workspace-search">
          <label className="wf-sr-only" htmlFor="workspace-search">
            Find a page
          </label>
          <input
            id="workspace-search"
            type="search"
            placeholder="Search workspace…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSearch('');
            }}
          />
          {search.trim() && (
            <nav aria-label="Search results" className="wf-search-results">
              {allLinks
                .filter((link) => link.label.toLowerCase().includes(search.trim().toLowerCase()))
                .map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => {
                      setSearch('');
                      setMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              {!allLinks.some((link) =>
                link.label.toLowerCase().includes(search.trim().toLowerCase()),
              ) && <p>No matching pages</p>}
            </nav>
          )}
        </div>
        <ThemeToggle />
        <span className="wf-user">
          <span className="wf-avatar" aria-hidden="true">
            {user?.displayName?.slice(0, 1).toUpperCase()}
          </span>
          <span>{user?.displayName}</span>
        </span>
      </header>
      <aside
        ref={sidebar}
        className={`wf-sidebar${menuOpen ? ' wf-sidebar--open' : ''}`}
        id="workspace-navigation"
        onKeyDown={(event) => {
          if (event.key === 'Escape') setMenuOpen(false);
        }}
      >
        <nav aria-label="Primary">
          {NAV_GROUPS.map((group) => (
            <section key={group.label}>
              <h2>{group.label}</h2>
              {group.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `wf-nav-link${isActive ? ' wf-nav-link--active' : ''}`
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  <WorkspaceIcon name={link.icon} />
                  {link.label}
                </NavLink>
              ))}
            </section>
          ))}
          {admin && (
            <section>
              <h2>Organisation</h2>
              <NavLink
                className="wf-nav-link"
                to="/system-audit"
                onClick={() => setMenuOpen(false)}
              >
                <WorkspaceIcon name="audit" />
                System Audit
              </NavLink>
              <NavLink className="wf-nav-link" to="/org/users" onClick={() => setMenuOpen(false)}>
                <WorkspaceIcon name="sales" />
                User Access
              </NavLink>
            </section>
          )}
        </nav>
        <button className="wf-logout" onClick={logout} type="button">
          Log out
        </button>
      </aside>
      {menuOpen && (
        <button
          type="button"
          className="wf-menu-backdrop"
          aria-label="Close navigation overlay"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <main className="rfp-page" id="main-content" tabIndex={-1}>
        <div className="wf-page-trail">
          <span>{group?.label ?? meta?.section ?? 'Workspace'}</span>
          <span aria-hidden="true">›</span>
          <span>
            {active?.label ??
              (location.pathname.startsWith('/simulation/')
                ? 'Report Simulation'
                : (meta?.page ?? 'Contract'))}
          </span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<LandingPage />} path="/" />
      <Route element={<LoginPage />} path="/login" />
      <Route element={<SignupPage />} path="/signup" />
      <Route element={<AccessHelpPage />} path="/request-access" />
      <Route element={<AccessHelpPage reset />} path="/reset-password" />
      <Route
        element={
          <RequireAuth>
            <ShellLayout />
          </RequireAuth>
        }
      >
        <Route element={<DashboardPage />} path="/dashboard" />
        <Route element={<UploadContractPage />} path="/upload" />
        <Route element={<UploadContractPage />} path="/upload/:contractId" />
        <Route element={<UploadsInProgressPage />} path="/uploads-in-progress" />
        <Route element={<AuthorisedContractsPage />} path="/authorised-contracts" />
        <Route element={<SimulationPage />} path="/simulation/:simulationId" />
        <Route element={<RightsPage />} path="/rights" />
        <Route element={<CrmPage />} path="/crm" />
        <Route element={<ApprovedReportsPage />} path="/approved-reports" />
        <Route
          element={
            <RequireAdmin>
              <SystemAuditPage />
            </RequireAdmin>
          }
          path="/system-audit"
        />
        {[
          'report-simulations',
          'general-ledger',
          'synced-erp',
          'calculator-rules',
          'insights',
          'catalog-intelligence',
          'contract-diff',
        ].map((page) => (
          <Route key={page} element={<WorkspaceFeaturePage page={page} />} path={`/${page}`} />
        ))}
        <Route
          element={
            <RequireAdmin>
              <UserAccessPage />
            </RequireAdmin>
          }
          path="/org/users"
        />
        <Route element={<PlaceholderPage title="requested" />} path="*" />
      </Route>
    </Routes>
  );
}

export default function AppShell() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
